import { type MemberInformation } from 'services'
import { isVacantStatus, isVacantPerson } from 'helpers'

interface ValidationResult {
  isValid: boolean
  message?: string
}

interface TermLimits {
  lowerLimit: number
  upperLimit: number
}

const ElectCodeKeys = {
  General: 'G',
  Primary: 'P',
  Special: 'S',
  Appointed: 'A'
}

export const termLimitsByStateId: Record<number, TermLimits> = {
  18: { lowerLimit: 3, upperLimit: 4 },
  19: { lowerLimit: 4, upperLimit: 2 }
}

export const validateDates = (position: MemberInformation): ValidationResult => {
  if (position.electDate !== '' && position.electDate !== undefined) {
    if (position.electCode === ElectCodeKeys.General) {
      const year = Number(position.electDate)
      if (isNaN(year) || position.electDate.length !== 4 || year < 1900) {
        return {
          isValid: false,
          message: 'Please enter a year only for first elect.'
        }
      }
    } else if (position.electCode === ElectCodeKeys.Special || position.electCode === ElectCodeKeys.Appointed) {
      if (position.electDate.match(/^\d{2}\/\d{2}\/\d{4}$/) == null) {
        return {
          isValid: false,
          message: 'Please enter a date for first elect using mm/dd/yyyy format.'
        }
      }
    }
  }
  return { isValid: true }
}

export const validateSeatStatus = (position: MemberInformation): ValidationResult => {
  if (position.legseat?.status?.id === undefined || position.legseat?.status?.id === 0) {
    return {
      isValid: false,
      message: 'Please select a seat status.'
    }
  }

  const isVacantStatusResult = isVacantStatus(position.legseat.status.statusCode)
  const isPersonVacant = isVacantPerson(position)

  if (isPersonVacant && !isVacantStatusResult) {
    return {
      isValid: false,
      message: 'This seat status must be a VAC selection.'
    }
  }

  if (!isPersonVacant && isVacantStatusResult) {
    return {
      isValid: false,
      message: 'This seat status must be an AOK selection.'
    }
  }

  return { isValid: true }
}

export const validateNumericFields = (position: MemberInformation): ValidationResult => {
  if (position.chamberRank !== undefined && isNaN(Number(position.chamberRank))) {
    return {
      isValid: false,
      message: 'Please enter a number for chamber rank.'
    }
  }

  if (position.percentVote !== undefined && position.percentVote !== null) {
    const voteNum = Number(position.percentVote)
    if (isNaN(voteNum) || voteNum < 0 || voteNum > 100) {
      return {
        isValid: false,
        message: 'Please enter a number between 0 and 100 for Last Vote percentage.'
      }
    }
  }

  const ratingFields = [
    { field: 'pyracusc', label: 'PYRACUSC' },
    { field: 'cyracusc', label: 'CYRACUSC' },
    { field: 'pyradasc', label: 'PYRADASC' },
    { field: 'cyradasc', label: 'CYRADASC' },
    { field: 'pyraflsc', label: 'PYRAFLSC' },
    { field: 'cyraflsc', label: 'CYRAFLSC' },
    { field: 'pyruscosc', label: 'PYRUSCOSC' },
    { field: 'cyruscosc', label: 'CYRUSCOSC' }
  ]

  for (const { field, label } of ratingFields) {
    const value = position[field as keyof MemberInformation]
    const num = Number(value)
    if (isNaN(num)) {
      return {
        isValid: false,
        message: `${label} must be a valid number.`
      }
    }
    if (num < 0 || num > 100) {
      return {
        isValid: false,
        message: `${label} must be between 0 and 100.`
      }
    }
  }

  return { isValid: true }
}
